'use client'

import { createContext, useContext, useEffect, useState, startTransition, ReactNode } from 'react'
import {
  PricingRegion,
  REGION_CONFIGS,
  RegionConfig,
  parseRegion,
  formatLocalPrice,
} from '@/lib/billing/region'

const STORAGE_KEY = 'bb_marketing_region'
const DEFAULT_REGION: PricingRegion = 'ZA'

type RegionContextValue = {
  region: PricingRegion
  setRegion: (r: PricingRegion) => void
  config: RegionConfig
  fmt: (amount: number) => string
}

const RegionContext = createContext<RegionContextValue>({
  region: DEFAULT_REGION,
  setRegion: () => {},
  config: REGION_CONFIGS[DEFAULT_REGION],
  fmt: (n) => formatLocalPrice(n, DEFAULT_REGION),
})

export function MarketingRegionProvider({ children }: { children: ReactNode }) {
  const [region, setRegionState] = useState<PricingRegion>(DEFAULT_REGION)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    startTransition(() => {
      const stored = parseRegion(localStorage.getItem(STORAGE_KEY))
      if (stored) {
        setRegionState(stored)
        setReady(true)
        return
      }
      const country = navigator.language.split('-')[1]?.toUpperCase()
      const inferred = parseRegion(country)
      if (inferred) setRegionState(inferred)
      setReady(true)
    })
  }, [])

  function setRegion(r: PricingRegion) {
    setRegionState(r)
    try { localStorage.setItem(STORAGE_KEY, r) } catch {}
  }

  const config = REGION_CONFIGS[region]
  const fmt = (amount: number) => formatLocalPrice(amount, region)

  // Suppress hydration mismatch by not rendering region-sensitive children until
  // client region is resolved. The layout wrapper shows children immediately;
  // individual components that call useRegion() will see the correct region.
  void ready

  return (
    <RegionContext.Provider value={{ region, setRegion, config, fmt }}>
      {children}
    </RegionContext.Provider>
  )
}

export function useRegion() {
  return useContext(RegionContext)
}
